/**
 * ONE list of business timezones, for every screen that asks for one.
 *
 * WHY THIS FILE EXISTS
 * There were two hardcoded copies — BusinessSettingsTab.js and Onboarding/CompanyBasicsStep.js
 * — and the second carried a comment saying "now matches the canonical list in
 * BusinessSettingsTab.js:137-146", which is a copy naming its own twin. Both listed the same
 * seven US zones, so a contractor in Canada could complete onboarding, be offered Canada as
 * their country one field earlier, and then have no timezone that described where they work.
 *
 * Values are IANA identifiers because that is what the rest of the stack already stores and
 * reads: company_settings.timezone, the company_tz() RPC, and every AT TIME ZONE in the
 * reporting SQL (CLAUDE.md §19). Verified live 2026-09-16 — all 286 stored values are IANA
 * and all of them appear below, so nothing is orphaned by adopting this list.
 *
 * Labels are what a contractor calls the zone, not what the database calls it. Arizona and
 * Saskatchewan are NOT duplicates of Mountain and Central: neither observes DST, so the
 * generic zone is wrong for them for most of the year. Same for Hawaii, and for Yukon, which
 * stopped changing its clocks in 2020.
 */

export const BUSINESS_TIMEZONE_GROUPS = [
  {
    country: 'United States',
    zones: [
      { value: 'America/New_York', label: 'Eastern Time (ET)' },
      { value: 'America/Chicago', label: 'Central Time (CT)' },
      { value: 'America/Denver', label: 'Mountain Time (MT)' },
      { value: 'America/Phoenix', label: 'Arizona — no DST (MST)' },
      { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
      { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
      { value: 'America/Adak', label: 'Hawaii–Aleutian Time (HAT)' },
      { value: 'Pacific/Honolulu', label: 'Hawaii — no DST (HST)' },
      { value: 'America/Puerto_Rico', label: 'Atlantic — Puerto Rico & USVI (AST)' },
    ],
  },
  {
    country: 'Canada',
    zones: [
      { value: 'America/St_Johns', label: 'Newfoundland Time (NT)' },
      { value: 'America/Halifax', label: 'Atlantic Time (AT)' },
      { value: 'America/Toronto', label: 'Eastern Time — Canada (ET)' },
      { value: 'America/Winnipeg', label: 'Central Time — Canada (CT)' },
      { value: 'America/Regina', label: 'Saskatchewan — no DST (CST)' },
      { value: 'America/Edmonton', label: 'Mountain Time — Canada (MT)' },
      { value: 'America/Whitehorse', label: 'Yukon — no DST (MST)' },
      { value: 'America/Vancouver', label: 'Pacific Time — Canada (PT)' },
    ],
  },
];

/** Flat list, for callers that do not want option groups. */
export const BUSINESS_TIMEZONES = BUSINESS_TIMEZONE_GROUPS.flatMap((g) => g.zones);

/**
 * The label for a stored IANA value, falling back to the identifier itself.
 *
 * The fallback matters: a company whose timezone was set before this list existed, or set by
 * an admin through some other route, must still render as SOMETHING rather than an empty
 * select that looks like it was never configured.
 */
export const timezoneLabel = (value) =>
  BUSINESS_TIMEZONES.find((z) => z.value === value)?.label || value || '';

// Minutes east of UTC for `tz` at `date`, from the zone's own wall clock.
const offsetMinutes = (tz, date) => {
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }).formatToParts(date).map((x) => [x.type, x.value]));
  return (Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute) - Math.floor(date.getTime() / 60000) * 60000) / 60000;
};

/**
 * The listed zone matching the timezone this device reports, or null.
 *
 * Jobber and HouseCall Pro start a new account on the user's own timezone; this wizard started
 * everyone on Los Angeles (the column default), so a contractor in Ohio who clicked through had
 * every date three hours off. Devices often report a zone that is not on the list —
 * America/Detroit, America/Indiana/Indianapolis, America/Boise — so an unlisted zone maps to the
 * listed one with the same winter AND summer offset (which also keeps Arizona and Saskatchewan,
 * which skip DST, apart from their neighbours), preferring the company's own country.
 */
export const detectBusinessTimezone = (country = '') => {
  let tz;
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return null; }
  if (!tz) return null;
  if (BUSINESS_TIMEZONES.some((z) => z.value === tz)) return tz;
  const year = new Date().getUTCFullYear();
  const jan = new Date(Date.UTC(year, 0, 15, 12));
  const jul = new Date(Date.UTC(year, 6, 15, 12));
  let target;
  try { target = [offsetMinutes(tz, jan), offsetMinutes(tz, jul)]; } catch { return null; }
  const preferCanada = String(country).trim().toUpperCase() === 'CANADA';
  const groups = [...BUSINESS_TIMEZONE_GROUPS].sort((a, b) => (preferCanada ? (a.country === 'Canada' ? -1 : b.country === 'Canada' ? 1 : 0) : 0));
  for (const g of groups) {
    const hit = g.zones.find((z) => offsetMinutes(z.value, jan) === target[0] && offsetMinutes(z.value, jul) === target[1]);
    if (hit) return hit.value;
  }
  return null;
};
